/**
 * El contexto de inclusion: las preguntas de seguimiento por tipo de
 * discapacidad, y como se leen en la hoja de vida.
 *
 * Las preguntas son las MISMAS del registro de personas con discapacidad de la
 * landing (landing-almia/src/lib/disability-questions.ts): mismos ids, mismas
 * opciones y mismo formato de respuesta. Asi lo que la persona contesto al
 * registrarse se puede traer tal cual a su hoja de vida, y una empresa lee lo
 * mismo en los dos sitios. La suite tiene una prueba que compara este catalogo
 * con el de la landing y falla si se separan.
 *
 * Formato de las respuestas (columna JSON `disability_details`):
 *
 *   { "<categoria>": { "<idPregunta>": "valor" | ["valor", ...],
 *                      "<idPregunta>OtherText": "texto libre de la opcion Otra" } }
 *
 * Este archivo no importa nada a proposito: docs-microservices tiene una copia
 * (src/utils/contextoInclusion.ts) para componer el PDF y el Word con los
 * mismos rotulos que se ven en pantalla.
 */

export type RespuestaInclusion = string | string[];
export type DetallesDiscapacidad = Record<string, Record<string, RespuestaInclusion>>;

export type TipoPreguntaInclusion = "single" | "multi" | "text";

export interface OpcionInclusion {
  value: string;
  label: string;
  /** Al marcarla aparece un texto libre, guardado en `<idPregunta>OtherText`. */
  hasFreeText?: boolean;
  /** Como se lee en la hoja si difiere de `label` ("No utilizo ninguno" → "Ninguno"). */
  cvLabel?: string;
}

export interface PreguntaInclusion {
  id: string;
  type: TipoPreguntaInclusion;
  /** La pregunta, tal cual la hace el registro. */
  prompt: string;
  /** El rotulo corto con que la respuesta aparece en la hoja de vida. */
  cvLabel: string;
  options?: OpcionInclusion[];
  helperText?: string;
}

export interface BloqueInclusion {
  category: string;
  questions: PreguntaInclusion[];
}

const narrativa = (id = "details"): PreguntaInclusion => ({
  id,
  type: "text",
  prompt: "Cuéntanos un poco más sobre tu discapacidad",
  cvLabel: "Descripción",
  helperText:
    "Con tus propias palabras, cuéntanos sobre tu condición, las características que consideres importantes y cualquier información que pueda ayudarnos a conocerte mejor. Esta información nos permitirá identificar oportunidades laborales que se ajusten mejor a tu perfil, habilidades y necesidades.",
});

export const PREGUNTAS_INCLUSION: Record<string, BloqueInclusion> = {
  Física: {
    category: "Física",
    questions: [
      {
        id: "bodyParts",
        type: "multi",
        prompt: "¿Qué parte o partes de tu cuerpo presentan alguna dificultad o limitación?",
        cvLabel: "Partes del cuerpo",
        options: [
          { value: "extremidadesSuperiores", label: "Extremidades superiores (brazos/manos)" },
          { value: "extremidadesInferiores", label: "Extremidades inferiores (piernas/pies)" },
          { value: "columnaEspalda", label: "Columna o espalda" },
          { value: "movilidadGeneral", label: "Movilidad general" },
          { value: "otra", label: "Otra", hasFreeText: true },
        ],
      },
      {
        id: "affects",
        type: "multi",
        prompt: "¿La dificultad afecta principalmente...?",
        cvLabel: "Afecta principalmente",
        options: [
          { value: "movimiento", label: "Movimiento o desplazamiento" },
          { value: "fuerza", label: "Fuerza" },
          { value: "coordinacion", label: "Coordinación" },
          { value: "equilibrio", label: "Equilibrio" },
          { value: "movimientosFinos", label: "Movimientos finos (manos/dedos)" },
          { value: "permanecerSentadoDePie", label: "Permanecer sentado/a o de pie durante períodos prolongados" },
          { value: "otra", label: "Otra", hasFreeText: true },
        ],
      },
      {
        id: "supports",
        type: "multi",
        prompt: "¿Utilizas algún apoyo o dispositivo para movilizarte o realizar actividades?",
        cvLabel: "Apoyos o dispositivos",
        options: [
          { value: "sillaRuedas", label: "Silla de ruedas" },
          { value: "baston", label: "Bastón" },
          { value: "muletas", label: "Muletas" },
          { value: "caminador", label: "Caminador" },
          { value: "protesis", label: "Prótesis" },
          { value: "ortesis", label: "Ortesis" },
          { value: "otro", label: "Otro", hasFreeText: true },
          { value: "ninguno", label: "No utilizo ninguno", cvLabel: "Ninguno" },
        ],
      },
      narrativa(),
    ],
  },
  Visual: {
    category: "Visual",
    questions: [
      {
        id: "condition",
        type: "single",
        prompt: "¿Cómo describirías tu condición visual?",
        cvLabel: "Condición visual",
        options: [
          { value: "ceguera", label: "Ceguera" },
          { value: "bajaVision", label: "Baja visión" },
          { value: "perdidaParcial", label: "Pérdida parcial de visión" },
          { value: "otra", label: "Otra", hasFreeText: true },
        ],
      },
      {
        id: "affects",
        type: "multi",
        prompt: "¿La dificultad visual afecta...?",
        cvLabel: "Afecta",
        options: [
          { value: "visionCerca", label: "Visión de cerca" },
          { value: "visionLejos", label: "Visión de lejos" },
          { value: "campoVisual", label: "Campo visual" },
          { value: "percepcionColores", label: "Percepción de colores" },
          { value: "pocaIluminacion", label: "Visión en ambientes con poca iluminación" },
          { value: "otra", label: "Otra", hasFreeText: true },
        ],
      },
      {
        id: "supports",
        type: "multi",
        prompt: "¿Utilizas alguna ayuda o tecnología para acceder a información?",
        cvLabel: "Ayudas o tecnologías",
        options: [
          { value: "lectorPantalla", label: "Lector de pantalla" },
          { value: "ampliadorPantalla", label: "Ampliador de pantalla" },
          { value: "lupa", label: "Lupa" },
          { value: "baston", label: "Bastón" },
          { value: "softwareAccesible", label: "Software o aplicaciones accesibles" },
          { value: "otra", label: "Otra", hasFreeText: true },
          { value: "ninguna", label: "No utilizo ninguna", cvLabel: "Ninguna" },
        ],
      },
      narrativa(),
    ],
  },
  Auditiva: {
    category: "Auditiva",
    questions: [
      {
        id: "lossType",
        type: "single",
        prompt: "¿Cómo describirías tu pérdida auditiva?",
        cvLabel: "Pérdida auditiva",
        options: [
          { value: "parcial", label: "Parcial" },
          { value: "total", label: "Total" },
          { value: "otra", label: "Otra", hasFreeText: true },
        ],
      },
      {
        id: "affectedEars",
        type: "single",
        prompt: "¿La pérdida auditiva se presenta en...?",
        cvLabel: "Se presenta en",
        options: [
          { value: "unOido", label: "Un oído" },
          { value: "ambosOidos", label: "Ambos oídos" },
        ],
      },
      {
        id: "supports",
        type: "multi",
        prompt: "¿Utilizas algún dispositivo o apoyo auditivo?",
        cvLabel: "Dispositivos o apoyos auditivos",
        options: [
          { value: "audifono", label: "Audífono" },
          { value: "implanteCoclear", label: "Implante coclear" },
          { value: "sistemaFm", label: "Sistema FM u otro dispositivo" },
          { value: "appsTranscripcion", label: "Aplicaciones o herramientas de transcripción" },
          { value: "otro", label: "Otro", hasFreeText: true },
          { value: "ninguno", label: "No utilizo ninguno", cvLabel: "Ninguno" },
        ],
      },
      {
        id: "communication",
        type: "multi",
        prompt: "¿Cómo te comunicas principalmente?",
        cvLabel: "Comunicación",
        options: [
          { value: "lenguaSenas", label: "Lengua de señas" },
          { value: "comunicacionOral", label: "Comunicación oral" },
          { value: "lecturaLabial", label: "Lectura labial" },
          { value: "escrituraChat", label: "Escritura / chat" },
          { value: "combinacion", label: "Una combinación de las anteriores" },
          { value: "otro", label: "Otro", hasFreeText: true },
        ],
      },
      narrativa(),
    ],
  },
  Intelectual: {
    category: "Intelectual",
    questions: [
      {
        id: "severity",
        type: "single",
        prompt: "¿Cómo describirías tu discapacidad cognitiva o intelectual?",
        cvLabel: "Grado",
        options: [
          { value: "leve", label: "Leve" },
          { value: "moderada", label: "Moderada" },
          { value: "severa", label: "Severa" },
          { value: "profunda", label: "Profunda" },
        ],
      },
      narrativa(),
    ],
  },
  Psicosocial: {
    category: "Psicosocial",
    questions: [
      {
        id: "manifestations",
        type: "multi",
        prompt: "¿Cómo se manifiesta tu discapacidad mental o psicosocial?",
        cvLabel: "Se manifiesta en",
        options: [
          { value: "concentracion", label: "Dificultades de concentración" },
          { value: "manejoEstres", label: "Manejo del estrés" },
          { value: "regulacionEmocional", label: "Regulación emocional" },
          { value: "interaccionSocial", label: "Interacción social" },
          { value: "comunicacion", label: "Comunicación" },
          { value: "adaptacionCambios", label: "Adaptación a cambios" },
          { value: "organizacionPlanificacion", label: "Organización y planificación" },
          { value: "manejoPresion", label: "Manejo de situaciones de presión" },
          { value: "procesamientoInformacion", label: "Procesamiento de información" },
          { value: "sensibilidadEstimulos", label: "Sensibilidad a determinados estímulos" },
          { value: "otra", label: "Otra", hasFreeText: true },
        ],
      },
      narrativa(),
    ],
  },
  Sordoceguera: {
    category: "Sordoceguera",
    questions: [
      {
        id: "visualCondition",
        type: "multi",
        prompt: "¿Cómo describirías tu condición visual?",
        cvLabel: "Condición visual",
        options: [
          { value: "ceguera", label: "Ceguera" },
          { value: "bajaVision", label: "Baja visión" },
          { value: "perdidaParcial", label: "Pérdida parcial de visión" },
          { value: "otra", label: "Otra", hasFreeText: true },
        ],
      },
      {
        id: "auditoryCondition",
        type: "multi",
        prompt: "¿Cómo describirías tu condición auditiva?",
        cvLabel: "Condición auditiva",
        options: [
          { value: "perdidaParcial", label: "Pérdida auditiva parcial" },
          { value: "perdidaTotal", label: "Pérdida auditiva total" },
          { value: "otra", label: "Otra", hasFreeText: true },
        ],
      },
      {
        id: "communication",
        type: "multi",
        prompt: "¿Cómo te comunicas principalmente?",
        cvLabel: "Comunicación",
        options: [
          { value: "lenguaSenas", label: "Lengua de señas" },
          { value: "lenguaSenasTactil", label: "Lengua de señas táctil" },
          { value: "comunicacionOral", label: "Comunicación oral" },
          { value: "escrituraChat", label: "Escritura / chat" },
          { value: "braille", label: "Braille" },
          { value: "tecnologiaDispositivos", label: "Tecnología o dispositivos de comunicación" },
          { value: "combinacion", label: "Una combinación de las anteriores" },
          { value: "otra", label: "Otra", hasFreeText: true },
        ],
      },
      {
        id: "supports",
        type: "multi",
        prompt: "¿Qué apoyos o tecnologías utilizas?",
        cvLabel: "Apoyos o tecnologías",
        options: [
          { value: "baston", label: "Bastón" },
          { value: "perroGuia", label: "Perro guía" },
          { value: "audifono", label: "Audífono" },
          { value: "implanteCoclear", label: "Implante coclear" },
          { value: "lectorPantalla", label: "Lector de pantalla" },
          { value: "ampliadorLupa", label: "Ampliador de pantalla / lupa" },
          { value: "braille", label: "Braille" },
          { value: "tecnologiaAccesibilidad", label: "Tecnología o aplicaciones de accesibilidad" },
          { value: "acompanamiento", label: "Acompañamiento de otra persona" },
          { value: "otro", label: "Otro", hasFreeText: true },
          { value: "ninguno", label: "No utilizo ninguno", cvLabel: "Ninguno" },
        ],
      },
      narrativa("condition"),
    ],
  },
  Múltiple: {
    category: "Múltiple",
    questions: [
      {
        id: "subcategories",
        type: "multi",
        prompt: "Seleccionaste discapacidad múltiple. ¿Qué tipos de discapacidad presentas?",
        cvLabel: "Tipos de discapacidad",
        options: [
          { value: "fisicaMotora", label: "Física / motora" },
          { value: "visual", label: "Visual" },
          { value: "auditiva", label: "Auditiva" },
          { value: "cognitivaIntelectual", label: "Cognitiva / intelectual" },
          { value: "mentalPsicosocial", label: "Mental / psicosocial" },
          { value: "delHablaComunicacion", label: "Del habla / comunicación" },
          { value: "otra", label: "Otra", hasFreeText: true },
        ],
      },
      narrativa(),
    ],
  },
};

/**
 * Subcategorias de "Múltiple" que despliegan las preguntas de otra categoria.
 * "delHablaComunicacion" y "otra" no tienen bloque propio, igual que en el
 * registro: quedan anotadas en `Múltiple.subcategories` y nada mas.
 */
const SUBCATEGORIA_A_CATEGORIA: Record<string, string> = {
  fisicaMotora: "Física",
  visual: "Visual",
  auditiva: "Auditiva",
  cognitivaIntelectual: "Intelectual",
  mentalPsicosocial: "Psicosocial",
};

/** Lo contrario: de varias categorias del perfil a las subcategorias de "Múltiple". */
const CATEGORIA_A_SUBCATEGORIA: Record<string, string> = Object.fromEntries(
  Object.entries(SUBCATEGORIA_A_CATEGORIA).map(([sub, cat]) => [cat, sub]),
);

/** Opciones retiradas del registro que pueden venir en respuestas antiguas del perfil. */
const ETIQUETAS_RETIRADAS: Record<string, string> = {
  prefiereNoEspecificar: "Prefiero no especificarlo",
  noSeguro: "No estoy seguro/a",
};

const esObjeto = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const textoLimpio = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

/**
 * Los bloques de preguntas que tocan para un tipo de discapacidad, en orden.
 *
 * Con "Múltiple" va primero su propio bloque (que pregunta cuales) y despues el
 * de cada subcategoria marcada: la misma resolucion que hace el registro.
 */
export const categoriasActivas = (
  tipo: string | null | undefined,
  detalles: DetallesDiscapacidad | null | undefined,
): string[] => {
  if (!tipo || !PREGUNTAS_INCLUSION[tipo]) return [];
  if (tipo !== "Múltiple") return [tipo];

  const marcadas = detalles?.["Múltiple"]?.subcategories;
  const subcategorias = Array.isArray(marcadas) ? marcadas : [];
  const desplegadas = subcategorias
    .map((s) => SUBCATEGORIA_A_CATEGORIA[s])
    .filter((c): c is string => Boolean(c));
  return ["Múltiple", ...Array.from(new Set(desplegadas))];
};

/**
 * Las respuestas en una forma valida, o null si no hay ninguna.
 *
 * Llega de una columna JSON que se escribe desde dos sitios (el creador y la
 * suite) y que Directus puede devolver como texto: se acepta solo texto y
 * listas de texto, y se descarta lo demas en vez de dejar que una forma
 * inesperada tumbe el documento.
 */
export const normalizarDetalles = (crudo: unknown): DetallesDiscapacidad | null => {
  let valor = crudo;
  if (typeof valor === "string") {
    try {
      valor = JSON.parse(valor);
    } catch {
      return null;
    }
  }
  if (!esObjeto(valor)) return null;

  const salida: DetallesDiscapacidad = {};
  for (const [categoria, respuestas] of Object.entries(valor)) {
    if (!esObjeto(respuestas)) continue;
    const limpias: Record<string, RespuestaInclusion> = {};
    for (const [clave, r] of Object.entries(respuestas)) {
      if (typeof r === "string") limpias[clave] = r;
      else if (Array.isArray(r)) limpias[clave] = r.filter((x): x is string => typeof x === "string");
    }
    if (Object.keys(limpias).length) salida[categoria] = limpias;
  }
  return Object.keys(salida).length ? salida : null;
};

const respondida = (r: RespuestaInclusion | undefined): boolean =>
  Array.isArray(r) ? r.length > 0 : Boolean(textoLimpio(r));

/**
 * Deja solo las respuestas que se ven en la hoja: las de las categorias activas
 * y, de cada pregunta, lo contestado. El texto libre de "Otra" se va si "Otra"
 * ya no esta marcada.
 *
 * Es lo que se guarda. Si la persona cambia de tipo de discapacidad, lo que
 * respondio para el anterior no se queda guardado a escondidas: son datos
 * sensibles y ya no estan en su hoja de vida.
 */
export const podarDetalles = (
  tipo: string | null | undefined,
  detalles: DetallesDiscapacidad | null | undefined,
): DetallesDiscapacidad | null => {
  if (!detalles) return null;
  const salida: DetallesDiscapacidad = {};

  for (const categoria of categoriasActivas(tipo, detalles)) {
    const respuestas = detalles[categoria];
    if (!respuestas) continue;
    const limpias: Record<string, RespuestaInclusion> = {};

    for (const pregunta of PREGUNTAS_INCLUSION[categoria]!.questions) {
      const r = respuestas[pregunta.id];
      if (!respondida(r)) continue;
      limpias[pregunta.id] = Array.isArray(r) ? [...r] : (r as string);

      const libre = textoLimpio(respuestas[`${pregunta.id}OtherText`]);
      const marcadas = Array.isArray(r) ? r : [r];
      const conTexto = (pregunta.options ?? []).some((o) => o.hasFreeText && marcadas.includes(o.value));
      if (libre && conTexto) limpias[`${pregunta.id}OtherText`] = libre;
    }
    if (Object.keys(limpias).length) salida[categoria] = limpias;
  }
  return Object.keys(salida).length ? salida : null;
};

export interface LineaContexto {
  etiqueta: string;
  valor: string;
  /** Texto escrito por la persona: va como parrafo, no en la misma linea. */
  narrativa: boolean;
}

export interface GrupoContexto {
  categoria: string;
  lineas: LineaContexto[];
}

const etiquetaOpcion = (pregunta: PreguntaInclusion, valor: string): string => {
  const opcion = pregunta.options?.find((o) => o.value === valor);
  return opcion?.cvLabel ?? opcion?.label ?? ETIQUETAS_RETIRADAS[valor] ?? valor;
};

/**
 * Las respuestas como se leen en la hoja: un grupo por categoria con sus
 * lineas "Rotulo: valor". El documento en pantalla, el PDF y el Word salen de
 * esta funcion, asi que los tres dicen lo mismo con las mismas palabras.
 */
export const lineasContexto = (
  tipo: string | null | undefined,
  detalles: DetallesDiscapacidad | null | undefined,
): GrupoContexto[] => {
  const podados = podarDetalles(tipo, detalles);
  if (!podados) return [];

  const grupos: GrupoContexto[] = [];
  for (const categoria of categoriasActivas(tipo, podados)) {
    const respuestas = podados[categoria];
    if (!respuestas) continue;
    const lineas: LineaContexto[] = [];

    for (const pregunta of PREGUNTAS_INCLUSION[categoria]!.questions) {
      const r = respuestas[pregunta.id];
      if (!respondida(r)) continue;

      if (pregunta.type === "text") {
        lineas.push({ etiqueta: pregunta.cvLabel, valor: textoLimpio(r), narrativa: true });
        continue;
      }

      const libre = textoLimpio(respuestas[`${pregunta.id}OtherText`]);
      const valores = (Array.isArray(r) ? r : [r as string]).map((v) => {
        const etiqueta = etiquetaOpcion(pregunta, v);
        const conTexto = pregunta.options?.find((o) => o.value === v)?.hasFreeText;
        return conTexto && libre ? `${etiqueta} (${libre})` : etiqueta;
      });
      lineas.push({ etiqueta: pregunta.cvLabel, valor: valores.join(", "), narrativa: false });
    }
    if (lineas.length) grupos.push({ categoria, lineas });
  }
  return grupos;
};

/**
 * Las respuestas del registro de la persona, adaptadas al tipo de su hoja.
 *
 * El perfil puede tener varias categorias sin "Múltiple"; la hoja de vida tiene
 * UN tipo, y con varias es "Múltiple". En ese caso se marcan como
 * subcategorias de "Múltiple" para que sus preguntas aparezcan.
 */
export const detallesDesdePerfil = (
  categorias: string[],
  detallesPerfil: unknown,
): { tipo: string | null; detalles: DetallesDiscapacidad | null } => {
  const validas = categorias.filter((c) => PREGUNTAS_INCLUSION[c]);
  if (validas.length === 0) return { tipo: null, detalles: null };

  const detalles: DetallesDiscapacidad = { ...(normalizarDetalles(detallesPerfil) ?? {}) };
  const tipo = validas.length === 1 ? validas[0]! : "Múltiple";

  if (tipo === "Múltiple") {
    const previas = detalles["Múltiple"]?.subcategories;
    const desdeCategorias = validas
      .map((c) => CATEGORIA_A_SUBCATEGORIA[c])
      .filter((s): s is string => Boolean(s));
    const subcategorias = Array.from(
      new Set([...(Array.isArray(previas) ? previas : []), ...desdeCategorias]),
    );
    detalles["Múltiple"] = { ...(detalles["Múltiple"] ?? {}), subcategories: subcategorias };
  }

  return { tipo, detalles: podarDetalles(tipo, detalles) };
};
